import { useEffect, useMemo, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import { buildCurrentActivity, chunkAccountIds, mergeActivityPayloads } from '../../lib/currentActivity';

// BL-177 Step 2j: loads the "Current activity" half of the Hybrid client cards for EVERY visible account in one go - never per card.
//
// One call to the database function crm_current_activity(account ids) answers all four sources together (trips, trips waiting on
// details, last message, open reservations). It runs under the viewer's OWN row-level security, so a conversation they cannot see
// (migration 212 conv_row_visible) contributes nothing; src/lib/currentActivity.js drops any flagged row once more. The ids go in chunks
// of 1000 (the function's own cap is 2000), which for any real book of clients is a single call.
//
// A quote's amount is a fare, which has its own permission-checked path (bl395a_engagement_prices, as the Engagements list uses):
// one more call for the quotes shown, and a viewer without fare access simply sees the quote without its amount.
//
//   accountIds  the accounts to describe (pass [] / enabled=false to load nothing - e.g. outside the Hybrid view)
//   returns     { activity: { [accountId]: model }, loading, error }   (an account that is absent has nothing: use EMPTY_ACTIVITY)
export default function useCurrentActivity(accountIds, { enabled = true, client = supabaseClient } = {}) {
    const key = useMemo(() => [...new Set((accountIds || []).filter(Boolean))].sort().join(','), [accountIds]);
    const [state, setState] = useState({ activity: {}, loading: false, error: null, key: '' });

    useEffect(() => {
        if (!enabled || !key) {
            setState((current) => (current.key === '' && !current.loading ? current : { activity: {}, loading: false, error: null, key: '' }));
            return undefined;
        }
        let cancelled = false;
        setState((current) => ({ ...current, loading: true, error: null }));
        (async () => {
            try {
                const results = await Promise.all(chunkAccountIds(key.split(',')).map((ids) => client.rpc('crm_current_activity', { p_account_ids: ids })));
                const failed = results.find((result) => result.error);
                if (failed) throw failed.error;
                const rows = mergeActivityPayloads(results.map((result) => result.data));

                let prices = {};
                const quoteIds = [...new Set(rows.trips.filter((trip) => trip.status === 'requested').map((trip) => trip.id))].slice(0, 500);
                if (quoteIds.length) {
                    const { data, error } = await client.rpc('bl395a_engagement_prices', { p_engagement_ids: quoteIds });
                    if (!error) prices = Object.fromEntries((data || []).map((row) => [row.engagement_id, row]));
                }
                if (!cancelled) setState({ activity: buildCurrentActivity({ ...rows, prices }), loading: false, error: null, key });
            } catch (error) {
                if (!cancelled) setState({ activity: {}, loading: false, error: error?.message || String(error), key });
            }
        })();
        return () => { cancelled = true; };
    }, [key, enabled, client]);

    return { activity: state.activity, loading: state.loading, error: state.error };
}
